import {
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React, { useEffect, useMemo, useState } from 'react'

import { getPreschoolTeacherTrends, getPreschoolUpdatesForDate } from 'api/preschoolUpdatesAudit'
import {
  DatePicker,
  DrillDownDialog,
  formatDate,
  latestWeekday,
  PRESENCE_FILTERS,
  StatStrip,
  TrendChart,
  TrendSummaryLine,
  UpdateCell,
  useDrillDown
} from 'app/pages/Audits/PreschoolDailyUpdates/PreschoolDailyUpdates'
import useStyles from 'app/pages/Audits/PreschoolDailyUpdates/styles'
import {
  computeChildMatrix,
  computeTeacherTrend,
  TEACHER_TREND_METRICS,
  TEACHER_TRENDS_URL,
  UPDATE_COLUMNS
} from 'app/pages/Audits/PreschoolDailyUpdates/data'

// Same static-preview approach as Audits > Preschool Daily Updates (see that
// page's data.js for the full explanation): everything here is pre-generated
// from real, one-off read-only DB audits, fetched at runtime from
// public/audit-data/.
//
// A teacher maps to exactly one centre in practice (confirmed against the
// data - see the "By teacher" centre filter on the Audits page), but a
// centre can have more than one teacher/room (e.g. Haralur has three). So
// the Day tab doesn't show the whole centre - it shows this teacher's own
// roster: every child she's posted at least one update for somewhere in the
// trends window, unioned across all ~22 days. That's a proxy for "her
// class," not a real room assignment (there's no room-to-teacher
// assignment table in the schema), so it can drift if a child moves rooms
// or a teacher is newly assigned - but it's far closer to "her kids" than
// showing everyone at the centre.
//
// The Trend tab is different: it's this teacher's own posting history
// specifically (late %, non-personalised %, as a rate of what they actually
// posted) - there's no "required" ground truth to trend against per
// teacher, only per centre/roster.

// Every centre a teacher has ever posted for, across the whole trends
// window - in practice this is a single centre; if it's ever more than one,
// picking the most frequent keeps the page from breaking on a hand-wavy
// assumption instead of silently showing the wrong room.
const resolveTeacherCentre = (teacherTrends, teacher) => {
  const counts = {}
  teacherTrends.dates.forEach((date) => {
    const centre = teacherTrends.byDate[date]?.[teacher]?.centre
    if (centre) counts[centre] = (counts[centre] || 0) + 1
  })
  const entries = Object.entries(counts)
  if (!entries.length) return null
  entries.sort((a, b) => b[1] - a[1])
  return entries[0][0]
}

const DayTab = ({ teacher, teacherCentre, roster, dateLabel, dayData, onDrillDown }) => {
  const classes = useStyles()
  const [presenceFilter, setPresenceFilter] = useState('ALL')

  if (!dayData) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
          Loading {dateLabel}...
        </Typography>
      </div>
    )
  }

  const rosterSet = new Set(roster)
  // Centre + roster says WHICH CHILDREN belong on this page. It does not say
  // whose work the update was, and leaving it there was the bug (OH-1751):
  // every centre has two or more teachers posting for the same children, so a
  // colleague's updates were credited to whoever the roster is filed under.
  // Ifath's updates all showed under Samiksha for exactly this reason.
  //
  // `postedBy` is the uploader and the backend has always sent it
  // (oh_moments/services/preschool_update_audit.py). The Audits page already
  // filters on it; this page never did.
  //
  // MISSING rows are kept deliberately. They are built when NO update exists
  // (postedBy is null by construction), so matching them against a name would
  // drop every one of them and empty the "never sent" tile — which is the other
  // half of what was reported. A missing update belongs to the roster-holder,
  // because it is the child who was missed, not a message someone sent.
  const rosterRows = dayData.rows.filter(
    (r) =>
      r.centre === teacherCentre &&
      rosterSet.has(r.child) &&
      (r.status === 'MISSING' || r.postedBy === teacher)
  )
  const rosterAbsentees = dayData.absentees.filter((a) => a.centre === teacherCentre && rosterSet.has(a.child))
  const children = computeChildMatrix(rosterRows, rosterAbsentees)
  const filteredChildren = children.filter((c) => {
    if (presenceFilter === 'PRESENT') return c.present
    if (presenceFilter === 'ABSENT') return !c.present
    return true
  })
  const presentCount = children.filter((c) => c.present).length

  return (
    <div>
      <StatStrip rows={rosterRows} onDrillDown={onDrillDown} />

      <div className={classes.filterRow}>
        {PRESENCE_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            clickable
            color={presenceFilter === f.key ? 'primary' : 'default'}
            onClick={() => setPresenceFilter(f.key)}
            className={classes.filterChip}
          />
        ))}
      </div>

      <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Child</TableCell>
              <TableCell>Present</TableCell>
              {UPDATE_COLUMNS.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChildren.map((c) => (
              <TableRow key={c.child}>
                <TableCell>{c.child}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={c.present ? 'Present' : 'Absent'}
                    className={c.present ? classes.chipOnTime : classes.chipAbsent}
                  />
                </TableCell>
                {UPDATE_COLUMNS.map((col) => (
                  <TableCell key={col.key}>
                    <UpdateCell child={c} column={col} onDrillDown={onDrillDown} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography className={classes.sampleNote}>
          {filteredChildren.length} of {children.length} children on this teacher&apos;s roster at {teacherCentre}
          {' '}on {dateLabel} ({presentCount} present, {children.length - presentCount} absent). Roster is every
          child she&apos;s posted for at least once across the whole trends window, not a real room assignment -
          it can miss a brand-new child or include one who moved rooms.
        </Typography>
      </TableContainer>
    </div>
  )
}

const TrendTab = ({ trendData, trendError, teacher }) => {
  const classes = useStyles()

  if (trendError) {
    return (
      <Paper variant="outlined" className={classes.anomalyNote}>
        <Typography variant="body2">{trendError}</Typography>
      </Paper>
    )
  }

  if (!trendData) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
          Computing the last 30 days of trends...
        </Typography>
      </div>
    )
  }

  return (
    <div>
      {TEACHER_TREND_METRICS.map((metric) => {
        const series = computeTeacherTrend(trendData, teacher, metric.key)
        return (
          <div key={metric.key} className={classes.trendBlock}>
            <Typography variant="subtitle2" className={classes.trendBlockTitle}>
              {metric.label}
            </Typography>
            <Paper variant="outlined" className={classes.trendCard}>
              {series.length > 0 ? (
                <TrendChart series={series} metricLabel={metric.label} higherIsBetter={metric.higherIsBetter} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No posted updates for {teacher} in this window yet.
                </Typography>
              )}
              <TrendSummaryLine series={series} metricLabel={metric.label} higherIsBetter={metric.higherIsBetter} />
            </Paper>
          </div>
        )
      })}
      <Typography className={classes.footerNote}>
        These rates are of what this teacher personally posted (their account), computed live over the last
        ~30 weekdays. Each chart splits at the midpoint of the available dates - not a regression, just two
        period averages.
      </Typography>
    </div>
  )
}

const UpdateCompliance = () => {
  const classes = useStyles()
  const [tab, setTab] = useState('day')
  const { drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  const [teacherTrends, setTeacherTrends] = useState(null)
  const [selectedDate, setSelectedDate] = useState(latestWeekday)
  const [selectedTeacher, setSelectedTeacher] = useState(null)
  const [dayData, setDayData] = useState(null) // { rows, absentees } | null
  const [trendData, setTrendData] = useState(null) // { dates, byDate } | null - live
  const [trendError, setTrendError] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetch(TEACHER_TRENDS_URL)
      .then((res) => res.json())
      .then((data) => {
        setTeacherTrends(data)
        setSelectedTeacher((cur) => cur || data.teachers[0])
      })
      .catch(() => setError('Could not load teacher trend history.'))
  }, [])

  // Trends are computed live from the backend over a rolling ~30-day window
  // (all teachers at once, in one call). Fetched lazily the first time the
  // Trend tab is opened - it's a heavier query than a single day, so there's
  // no reason to pay for it on a page that only ever views the Day tab.
  useEffect(() => {
    if (tab !== 'trend' || trendData || trendError) return
    getPreschoolTeacherTrends()
      .then(setTrendData)
      .catch(() => setTrendError('Could not load live teacher trends.'))
  }, [tab, trendData, trendError])

  // Per-date rows are computed live from the backend (same endpoint the Audits
  // page uses), so any past date works - not just the handful of static files.
  // The teacher roster/centre still come from the static teacherTrends above.
  useEffect(() => {
    if (!selectedDate) return
    setDayData(null)
    setError(null)
    getPreschoolUpdatesForDate(selectedDate)
      .then(setDayData)
      .catch(() => setError(`Could not load audit data for ${selectedDate}.`))
  }, [selectedDate])

  const teacherCentre = useMemo(
    () => (teacherTrends && selectedTeacher ? resolveTeacherCentre(teacherTrends, selectedTeacher) : null),
    [teacherTrends, selectedTeacher]
  )

  const loading = !teacherTrends || !selectedTeacher

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Daily Updates Report
        </Typography>
      </div>

      {error && (
        <Paper variant="outlined" className={classes.anomalyNote}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {loading && !error ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="textSecondary" style={{ marginLeft: 12 }}>
            Loading teachers...
          </Typography>
        </div>
      ) : (
        !error && (
          <>
            <div className={classes.teacherPickerRow}>
              <Autocomplete
                options={teacherTrends.teachers}
                value={selectedTeacher}
                onChange={(e, v) => v && setSelectedTeacher(v)}
                disableClearable
                className={classes.teacherAutocomplete}
                renderInput={(params) => <TextField {...params} label="Teacher" variant="outlined" size="small" />}
              />
              {tab === 'day' && (
                <DatePicker selectedDate={selectedDate} onChange={setSelectedDate} />
              )}
            </div>

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 24, borderBottom: '2px solid #eee' }}
            >
              <Tab value="day" label="Day" style={{ textTransform: 'none' }} />
              <Tab value="trend" label="Trend" style={{ textTransform: 'none' }} />
            </Tabs>

            {tab === 'day' && (
              <DayTab
                teacher={selectedTeacher}
                teacherCentre={teacherCentre}
                roster={teacherTrends.rosters[selectedTeacher] || []}
                dateLabel={formatDate(selectedDate)}
                dayData={dayData}
                onDrillDown={openDrillDown}
              />
            )}
            {tab === 'trend' && (
              <TrendTab trendData={trendData} trendError={trendError} teacher={selectedTeacher} />
            )}
          </>
        )
      )}

      {drillDown && (
        <DrillDownDialog
          open={Boolean(drillDown)}
          onClose={closeDrillDown}
          title={drillDown.title}
          rows={drillDown.rows}
        />
      )}
    </div>
  )
}

export default UpdateCompliance
